// src/components/layout/Head.tsx
import { Helmet } from 'react-helmet-async';
import { useTranslations } from '@/hooks/useTranslations';
import { generateMetaTags, generateStructuredData } from '@/config/seo';
import { getColorNameForLanguage } from '@/config/colors';

interface HeadProps {
  color?: string;
}

const Head = ({ color = '#FFFFFF' }: HeadProps) => {
  const { currentLang } = useTranslations();
  const colorName = getColorNameForLanguage(color, currentLang);
  
  const metaTags = generateMetaTags(currentLang, color, colorName);
  const structuredData = generateStructuredData(currentLang, color, colorName);

  return (
    <Helmet>
      <html lang={currentLang} />
      <title>{metaTags.title}</title>
      <meta name="description" content={metaTags.description} />
      <meta name="keywords" content={metaTags.keywords} />
      <link rel="canonical" href={metaTags.canonical} />
      
      {/* Open Graph Tags */}
      <meta property="og:title" content={metaTags.title} />
      <meta property="og:description" content={metaTags.description} />
      <meta property="og:url" content={metaTags.canonical} />
      <meta property="og:type" content="website" />
      <meta property="og:image" content={metaTags.ogImage} />
      <meta property="og:locale" content={currentLang === 'en' ? 'en_US' : currentLang} />
      
      {/* Twitter Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={metaTags.title} />
      <meta name="twitter:description" content={metaTags.description} />
      <meta name="twitter:image" content={metaTags.ogImage} />
      
      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>
    </Helmet>
  );
};

export default Head;